/**
 * Helper per formattare risposte ai sondaggi nella vista admin.
 * Gestisce: tutti i tipi di domanda (radio, checkbox, matrix, file, signature, ranking, ecc.)
 * Gestisce visibilità condizionale (showIf) per non segnare come "mancanti" le domande
 * che non dovevano essere mostrate al singolo rispondente.
 */

export interface SurveyQuestionOption {
  optionId: string;
  text: string;
  value?: string | null;
}

export interface SurveyMatrixRow { rowId: string; text: string }
export interface SurveyMatrixCol { colId: string; text: string; value?: string | null }

export interface SurveyQuestion {
  questionId: string;
  type: string;
  text: string;
  options?: SurveyQuestionOption[];
  matrixConfig?: {
    rows?: SurveyMatrixRow[];
    columns?: SurveyMatrixCol[];
  } | null;
  showIf?: {
    questionId: string;
    operator: 'equals' | 'not_equals' | 'contains' | 'not_contains' | 'is_empty' | 'is_not_empty';
    value?: string;
    action?: 'show' | 'hide';
  } | null;
}

export interface SurveyAnswerTerritorio {
  livello?: string | null;
  regione?: string | null;
  provincia: string;
}

export interface SurveyAnswer {
  questionId: string;
  value: string | null;
  stepIndex?: number | null;
  /** Solo la domanda «Territorio di competenza»: una voce per provincia. */
  territorio?: SurveyAnswerTerritorio | null;
  /** Prodotto da buildAnswerMap per la domanda territoriale: tutte le voci, nell'ordine. */
  voci?: SurveyAnswer[];
}

export interface VoceTerritorio {
  provincia: string;
  regione: string | null;
  risposta: string;
}

/**
 * Prova a fare JSON.parse di un valore. Se fallisce restituisce il valore raw.
 */
export const tryParseJSON = (raw: unknown): unknown => {
  if (raw === null || raw === undefined) return raw;
  if (typeof raw !== 'string') return raw;
  const s = raw.trim();
  if (!s) return s;
  if ((s.startsWith('[') && s.endsWith(']')) || (s.startsWith('{') && s.endsWith('}'))) {
    try { return JSON.parse(s); } catch { return raw; }
  }
  return raw;
};

/**
 * Mappa optionId → testo dell'opzione (per radio/checkbox/dropdown)
 */
export const getOptionLabel = (question: SurveyQuestion | undefined, raw: unknown): string => {
  if (raw === null || raw === undefined) return '';
  const str = String(raw);
  if (!question || !Array.isArray(question.options) || question.options.length === 0) return str;
  const found = question.options.find(o => o.optionId === str || o.value === str || o.text === str);
  return found ? found.text : str;
};

/**
 * Valuta la condizione showIf per stabilire se la domanda dovrebbe essere visibile al rispondente.
 * answersByQuestionId è una mappa derivata dalle answers del rispondente.
 */
export const isQuestionVisible = (
  question: SurveyQuestion,
  answersByQuestionId: Map<string, SurveyAnswer>
): boolean => {
  if (!question.showIf || !question.showIf.questionId) return true;
  const { questionId, operator, value, action = 'show' } = question.showIf;
  const dep = answersByQuestionId.get(questionId);
  const depValue = dep ? tryParseJSON(dep.value) : null;

  const norm = (v: unknown): string => {
    if (v === null || v === undefined) return '';
    if (Array.isArray(v)) return v.join('|');
    if (typeof v === 'object') return JSON.stringify(v);
    return String(v);
  };

  const depStr = norm(depValue);
  const targetStr = norm(value);

  let met = false;
  switch (operator) {
    case 'equals': met = depStr === targetStr; break;
    case 'not_equals': met = depStr !== targetStr; break;
    case 'contains': met = depStr.includes(targetStr); break;
    case 'not_contains': met = !depStr.includes(targetStr); break;
    case 'is_empty': met = !depStr; break;
    case 'is_not_empty': met = !!depStr; break;
    default: met = false;
  }

  return action === 'show' ? met : !met;
};

/**
 * Voci della domanda «Territorio di competenza» di un rispondente:
 * provincia, regione e testo dell'opzione scelta, nell'ordine salvato.
 * Accetta l'entry di buildAnswerMap (con `voci`) o una voce singola.
 */
export const vociTerritorio = (question: SurveyQuestion, answer: SurveyAnswer | undefined): VoceTerritorio[] => {
  if (!answer) return [];
  const voci = answer.voci ?? (answer.territorio ? [answer] : []);
  return voci
    .filter(v => v.territorio?.provincia && v.value !== null && v.value !== undefined && v.value !== '')
    .map(v => ({
      provincia: v.territorio!.provincia,
      regione: v.territorio?.regione ?? null,
      risposta: getOptionLabel(question, v.value)
    }));
};

/**
 * Formatta il valore di una risposta come stringa leggibile (per tabelle, CSV/Excel).
 */
export const formatAnswer = (question: SurveyQuestion, answer: SurveyAnswer | undefined): string => {
  // Territorio di competenza: «AQ: 7 · CH: 16-20»
  if (question.type === 'territorio') {
    const voci = vociTerritorio(question, answer);
    if (voci.length > 0) return voci.map(v => `${v.provincia}: ${v.risposta}`).join(' · ');
    return answer?.value ? getOptionLabel(question, answer.value) : '';
  }

  if (!answer || answer.value === null || answer.value === undefined || answer.value === '') {
    return '';
  }

  const parsed = tryParseJSON(answer.value);
  const type = question.type;

  // Signature
  if (type === 'signature') return 'Firma acquisita';

  // File: il backend serializza fileInfo separatamente; qui mostriamo solo il valore raw
  if (type === 'file') return String(answer.value);

  // Matrix / matrix_multi
  if (type === 'matrix' || type === 'matrix_multi') {
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      const rows = question.matrixConfig?.rows || [];
      const cols = question.matrixConfig?.columns || [];
      const rowLabel = (rid: string) => rows.find(r => r.rowId === rid)?.text || rid;
      const colLabel = (cid: string) => cols.find(c => c.colId === cid)?.text || cid;
      return Object.entries(parsed as Record<string, unknown>)
        .map(([rid, cid]) => {
          const sel = Array.isArray(cid)
            ? cid.map(c => colLabel(String(c))).join(', ')
            : colLabel(String(cid));
          return `${rowLabel(rid)}: ${sel}`;
        })
        .join(' · ');
    }
    return String(answer.value);
  }

  // Array (checkbox / ranking)
  if (Array.isArray(parsed)) {
    return parsed.map(v => getOptionLabel(question, v)).join(', ');
  }

  // Checkbox serializzato come "a,b,c"
  if (type === 'checkbox' && typeof parsed === 'string' && parsed.includes(',')) {
    return parsed.split(',').map(v => getOptionLabel(question, v.trim())).join(', ');
  }

  // Radio / Dropdown: mappa l'optionId al testo
  if (type === 'radio' || type === 'dropdown') {
    return getOptionLabel(question, parsed);
  }

  // Default
  return String(parsed);
};

/**
 * Estrae la lista ordinata di domande di un sondaggio (esclude tipi display-only).
 */
export const flattenSurveyQuestions = (
  survey: { steps?: { questions?: SurveyQuestion[] }[] } | null | undefined,
  excludeTypes: string[] = ['html']
): SurveyQuestion[] => {
  if (!survey?.steps) return [];
  const out: SurveyQuestion[] = [];
  for (const step of survey.steps) {
    for (const q of (step.questions || [])) {
      if (!excludeTypes.includes(q.type)) out.push(q);
    }
  }
  return out;
};

/**
 * Crea mappa { questionId → answer } per accesso rapido.
 * La domanda territoriale ha una voce per provincia: finiscono tutte in
 * `voci` di un'unica entry, il cui `value` le unisce con la virgola (come il
 * server quando valuta le condizioni showIf).
 */
export const buildAnswerMap = (answers: SurveyAnswer[] | undefined): Map<string, SurveyAnswer> => {
  const map = new Map<string, SurveyAnswer>();
  (answers || []).forEach(a => {
    if (!a.territorio) {
      map.set(a.questionId, a);
      return;
    }
    const prev = map.get(a.questionId);
    if (prev?.voci) {
      prev.voci.push(a);
      prev.value = prev.voci.map(v => v.value ?? '').join(',');
    } else {
      map.set(a.questionId, {
        questionId: a.questionId,
        stepIndex: a.stepIndex ?? null,
        value: a.value,
        voci: [a]
      });
    }
  });
  return map;
};

/**
 * Compilazione sul sito: risposte per domanda + risposte per provincia della
 * domanda territoriale in un'unica mappa { questionId → valore }, per valutare
 * le condizioni «Mostra se». Il valore della domanda territoriale unisce le
 * province compilate con la virgola, come il server (`visibileDaShowIf`).
 */
export const mappaRisposteConTerritorio = (
  answers: Record<string, string>,
  domandaTerritorioId: string | null | undefined,
  territorioAnswers: Record<string, string>
): Record<string, string> => {
  // Sempre una copia: chi la riceve (es. clearHiddenAnswers) può modificarla
  if (!domandaTerritorioId) return { ...answers };
  const valore = Object.values(territorioAnswers || {}).filter(v => !!v).join(',');
  return { ...answers, [domandaTerritorioId]: valore };
};
